import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useLlmSelection } from "@/hooks/useLlmSelection";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { CheckIcon, Pencil2Icon } from "@radix-ui/react-icons";
import {
  CompanyDTO,
  ConversationDTO,
  ConversationMessagePromptInfo,
  CursorPaginatedResponse,
} from "@shaarp/common";
import { PanelRightIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { MdClose } from "react-icons/md";
import { RxExternalLink, RxListBullet } from "react-icons/rx";
import { NavLink } from "react-router-dom";
import { useChatMessageListener } from "@/hooks/useChatActions";
import { useChatStream } from "@/contexts/chat-stream-context";
import { CompanyPanelSurfaceProvider } from "@/contexts/company-panel-surface-context";
import { CurrentCompanyMentionProvider } from "@/contexts/current-company-mention-context";
import type { CompanyPreviewPanelSurface } from "@/stores/company-preview-panel-store";
import { MemoryMessageToSend } from "../chat/chat-conversation";
import { ChatLayout } from "../chat/chat-layout";
import { Button, buttonVariants } from "../ui/button";
import { CompanyPreviewPanelInline } from "./company-preview-panel-inline";

export default function CompanyChat({
  company,
  onClose = () => {},
  initialMessage = null,
  onInitialMessageConsumed,
  isModal = false,
  onMoveToSidePanel,
  onConversationChange,
  resumeConversationId,
  panelSurface = "sheet",
}: {
  company?: CompanyDTO;
  onClose?: () => void;
  initialMessage?: MemoryMessageToSend | null;
  onInitialMessageConsumed?: () => void;
  isModal?: boolean;
  onMoveToSidePanel?: () => void;
  onConversationChange?: (conversationId: string | undefined) => void;
  resumeConversationId?: string | null;
  /**
   * Surface a clicked company mention opens its side panel on. "inline" renders
   * the preview inside this chat's own bounds (the docked company-page side
   * panel); "sheet" (default) uses the viewport-anchored global sheet — kept for
   * the modal chat.
   */
  panelSurface?: CompanyPreviewPanelSurface;
}) {
  const api = useApi();
  const { isDesktop } = useDeviceFormat();
  const { t } = useTranslation();
  const toastError = useToastError();

  const { llmSelection } = useLlmSelection();

  const { stopStreamBeforeSend } = useChatStream();

  const [companyIdFetched, setCompanyIdFetched] = useState<string>();
  const [conversations, setConversations] = useState<ConversationDTO[]>();
  const [conversation, setConversation] = useState<ConversationDTO>();
  const [pendingMessage, setPendingMessage] =
    useState<MemoryMessageToSend | null>(null);
  const [isCreatingConversation, setIsCreatingConversation] = useState(!!initialMessage);
  const isCreatingFromInitialMessage = useRef(!!initialMessage);

  useEffect(() => {
    if (initialMessage) {
      isCreatingFromInitialMessage.current = true;
      setIsCreatingConversation(true);
      createNewConversation(initialMessage);
      onInitialMessageConsumed?.();
    }
  }, [initialMessage]);

  useEffect(() => {
    onConversationChange?.(conversation?.id);
  }, [conversation?.id]);

  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;

  const isCreatingRef = useRef(false);

  // Listen for CTA quick-prompt messages
  const handleChatActionMessage = useCallback(
    (message: string, prompt?: ConversationMessagePromptInfo) => {
      stopStreamBeforeSend().then(() => {
        const messageToSend: MemoryMessageToSend = {
          llmId: llmSelection ?? "auto",
          text: message,
          filesAttached: [],
          prompt,
        };

        if (conversationRef.current) {
          // Conversation already exists — push via pendingMessage prop
          setPendingMessage(messageToSend);
        } else {
          // No conversation yet — create one
          createNewConversation(messageToSend);
        }
      });
    },
    [llmSelection]
  );

  useChatMessageListener(handleChatActionMessage);

  useEffect(() => {
    if (!company) {
      fetchAllConversations();
    } else if (
      !companyIdFetched ||
      (company && company.id !== companyIdFetched)
    ) {
      fetchCompanyConversations();
      setCompanyIdFetched(company.id);
    }
  }, [company]);

  // When resumeConversationId changes, try to select it from existing data or re-fetch
  useEffect(() => {
    if (!resumeConversationId) return;
    if (conversation?.id === resumeConversationId) return;

    const existing = conversations?.find(
      (conv) => conv.id === resumeConversationId,
    );
    if (existing) {
      setConversation(existing);
    } else if (company) {
      fetchCompanyConversations();
    } else {
      fetchAllConversations();
    }
  }, [resumeConversationId]);

  const findResumeConversation = (data: ConversationDTO[]): ConversationDTO | undefined => {
    if (resumeConversationId) {
      return data.find((conv) => conv.id === resumeConversationId);
    }
    return undefined;
  };

  const fetchAllConversations = async () => {
    try {
      const { data } = await api.get<CursorPaginatedResponse<ConversationDTO>>(`/conversations`);
      const items = Array.isArray(data.data) ? data.data : [];
      setConversations(items);
      if (items.length > 0 && !isCreatingFromInitialMessage.current) {
        const resumeConv = findResumeConversation(items);
        if (resumeConv) {
          setConversation(resumeConv);
        } else if (!conversation) {
          setConversation(items[0]);
        } else {
          const conv = items.find((conv) => conv.id === conversation.id);
          if (conv) {
            setConversation(conv);
          } else {
            setConversation(items[0]);
          }
        }
      }
    } catch (error) {
      toastError();
    }
  };

  const fetchCompanyConversations = async () => {
    if (!company) return;
    if (isCreatingRef.current) return;

    try {
      const { data } = await api.get<CursorPaginatedResponse<ConversationDTO>>(`/conversations`, {
        params: {
          companyId: company.id,
        },
      });

      // Don't overwrite conversation state if a creation is in progress
      if (isCreatingRef.current) return;

      const items = Array.isArray(data.data) ? data.data : [];

      if (!isCreatingFromInitialMessage.current) {
        const resumeConv = findResumeConversation(items);
        if (resumeConv) {
          setConversation(resumeConv);
        } else if (!conversation && items.length) {
          setConversation(items[0]);
        } else if (conversation && items.length) {
          const conv = items.find((conv) => conv.id === conversation.id);
          if (conv) {
            setConversation(conv);
          }
        }
      }

      setConversations(items);
    } catch (error) {
      toastError();
    }
  };

  const selectConversation = (conv: ConversationDTO) => {
    setConversation(conv);
  };

  const createNewConversation = async (message?: MemoryMessageToSend) => {
    isCreatingRef.current = true;
    // Show the message optimistically while the conversation is created.
    if (message) {
      setPendingMessage(message);
    }
    setIsCreatingConversation(true);
    try {
      const { data } = await api.post<ConversationDTO>("/conversation", {
        llmId: message?.llmId || llmSelection,
        message: message?.text || null,
        ...(company?.id && { companyIdTarget: company.id }),
      });

      // Immediately set the new conversation as the selected one
      setConversation(data);
      isCreatingFromInitialMessage.current = false;
      setIsCreatingConversation(false);

      // Add the new conversation to the list at the beginning
      setConversations((prev) => (prev ? [data, ...prev] : [data]));
    } catch {
      isCreatingFromInitialMessage.current = false;
      setIsCreatingConversation(false);
      setPendingMessage(null);
      toastError({ description: t("company.chat.errorCreatingConversation") });
    } finally {
      isCreatingRef.current = false;
    }
  };

  const onMessageGenerated = async () => {
    if (company) {
      fetchCompanyConversations();
    } else {
      fetchAllConversations();
    }
  };

  return (
    <CompanyPanelSurfaceProvider surface={panelSurface}>
      <CurrentCompanyMentionProvider company={company ?? null}>
      <div
        className={cn("flex absolute top-0 left-0 z-10 flex-col w-full h-full")}
      >
        <div className="flex z-20 justify-between items-center p-1 w-full border-b bg-background">
          <div className="p-3 font-bold">
            {conversation?.title || t("company.chat.conversation")}
          </div>
          <div className="flex items-center pr-3 space-x-2">
            <Tooltip>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={!conversations?.length}
                    >
                      <RxListBullet className="size-5" />
                    </Button>
                  </TooltipTrigger>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {conversations?.map((conv) => (
                    <DropdownMenuItem
                      key={conv.id}
                      className="flex-row"
                      onClick={() => selectConversation(conv)}
                    >
                      <div className="flex-1">
                        {conv.title || t("company.chat.conversation")}
                      </div>
                      <CheckIcon
                        className={cn(
                          "ml-3 h-4 w-4",
                          conversation && conv.id === conversation.id
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <TooltipContent side="bottom">
                {t("company.chat.listConversations")}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setConversation(undefined)}
                >
                  <Pencil2Icon className="size-4" />
                </Button>
              </TooltipTrigger>
              {isDesktop && (
                <TooltipContent side="bottom">
                  {t("company.chat.newConversation")}
                </TooltipContent>
              )}
            </Tooltip>
            {isDesktop && (
              <>
                {onMoveToSidePanel && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={onMoveToSidePanel}
                      >
                        <PanelRightIcon className="size-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {t("company.chat.moveToSidePanel")}
                    </TooltipContent>
                  </Tooltip>
                )}
                {!isModal && (
                  <>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <NavLink
                          className={cn(
                            buttonVariants({ variant: "ghost", size: "icon" }),
                            !conversation && "opacity-50"
                          )}
                          to={`/chat/${conversation?.id}`}
                          target="_blank"
                          onClick={(e) => {
                            if (!conversation) {
                              e.preventDefault();
                            }
                          }}
                        >
                          <RxExternalLink className="size-4" />
                        </NavLink>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {t("company.chat.openInChat")}
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" onClick={onClose}>
                          <MdClose className="size-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {t("company.chat.closeConversation")}
                      </TooltipContent>
                    </Tooltip>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <div className="relative flex-1">
          <ChatLayout
            conversation={conversation}
            pendingMessage={pendingMessage}
            triggerCreateNewConversation={createNewConversation}
            onMessageGenerated={onMessageGenerated}
            onPendingMessageSent={() => setPendingMessage(null)}
            companyTargeted={company ?? null}
            isCreatingConversation={isCreatingConversation}
          />
        </div>
        {/* When docked as a side panel, company mentions open here — filling the
            chat's own bounds — instead of the viewport-anchored global sheet.
            Inert unless the store's surface is "inline". */}
        <CompanyPreviewPanelInline />
      </div>
      </CurrentCompanyMentionProvider>
    </CompanyPanelSurfaceProvider>
  );
}
